// OrderFlowMyAccount.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from '../../redux/actions/authActions';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import './MyReceipts.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign, faGift } from '@fortawesome/free-solid-svg-icons';

const OrderFlowMyAccount = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();
  const [mlmAccountBalanceAmount, setMLMAccountBalanceAmount] = useState(0.00);
  const [mlmAccountBalancePoints, setMLMAccountBalancePoints] = useState(0);
  const [mlmAccountLastUpdated, setMLMAccountLastUpdated] = useState();
  const [mlmAccountLastUpdatedRemarks, setMLMAccountLastUpdatedRemarks] = useState('');

  const [mlmAccountData, setMLMAccountData] = useState([]);
  const [selectedMLMAccountId, setSelectedMLMAccountId] = useState([]);
  const [userName, setUserName] = useState(authState.user.name);    
  const [userId, setUserId] = useState(authState.user._id);
  const [myMLMRequests, setMyMLMRequests] = useState([]);
  const [selectedMLMRequestId, setSelectedMLMRequestId] = useState(null);
  const [showItineraryClicked, setShowItineraryClicked] = useState(false);
  const isLoggedIn = authState?.isLoggedIn || false;

  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
      dispatch(logout());
      navigate("/orderflow-home");
  };

  //MLMAccount Transactions
  const fetchMLMAccount = useCallback(() => {
    const config = {
      url: "/mlmaccounts/my",
      method: "post",
      headers: { Authorization: authState.token },
      data: {userCode: authState.user.think5whyscode}
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        // console.log("Response:", response);
        if (response && Array.isArray(response.mlmMyAccounts)) {
          setMLMAccountData(response.mlmMyAccounts);
        } else {
          console.error("Invalid response format: MLMAccount transaction data not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching mlmAccounts:", error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchMLMAccount();
  }, [authState.isLoggedIn, fetchMLMAccount]);

  // const sortedMLMAccountData = [...mlmAccountData].sort((a, b) => new Date(b.dateTransaction) - new Date(a.dateTransaction));

  const handleMLMAccountView = (_id) => {
    setSelectedMLMAccountId(prevId => (prevId === _id ? null : _id));
  };

  const getTransactionType = (isAdvice, isPayment) => {
    if (isAdvice) return 'Earning (Debit)';
    if (isPayment) return 'Payment Received (Credit)';
    return 'Unknown';
  };

  const formatDateTime = (date) => {
    const options = {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const sortedMLMAccountData = [...mlmAccountData].sort((a, b) => new Date(a.date) - new Date(b.date));

  let runningBalance = 0;
  const balanceData = [];

  sortedMLMAccountData.forEach((mlmAccountItem) => {
    const amount = mlmAccountItem.amount || 0;
    runningBalance += mlmAccountItem.isPayment ? amount : -amount;
    balanceData.push({ ...mlmAccountItem, balance: runningBalance });
  });

  const displayData = [...balanceData].reverse();

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-gray-100">
        <header className="sticky top-0 z-50 bg-black border border-cyan-400 px-4 py-2 flex flex-row items-center justify-between shadow-lg">
            <div className='flex flex-col items-start justify-start'>
                <h2 className='text-xl lg:text-2xl font-bold text-yellow-200 text-center mb-2'>
                    <i className="fas fa-shopping-cart text-xl lg:text-2xl text-yellow-200"></i>Khareedo
                </h2>
                <h2 className='text-sm font-bold text-white text-center mb-2'>
                    My MLM Account
                </h2> 
            </div>  
            <div>
              <div className="hidden md:flex items-center gap-3">
                  {isLoggedIn ? (
                      <>
                          <div className="text-xs text-left text-blue-300">{userName}</div>
                          <button
                              onClick={handleLogout}
                              className="p-2 mt-2 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                              title="Logout"
                          >
                              ⏻
                          </button>
                      </>
                  ) : (
                      <>
                          <Link
                              to="/login"
                              className="px-3 py-1 rounded-full border border-blue-700 text-sm text-blue-200 hover:bg-blue-900"
                          >
                              Login
                          </Link>
                          <Link
                              to="/signup"
                              className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm hover:bg-blue-400"
                          >
                              Register
                          </Link>
                      </>
                  )}
              </div>
          
              {/* Mobile hamburger */}
              <button
                  onClick={() => setIsMobileMenuOpen((s) => !s)}
                  className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-blue-300 bg-blue-500"
                  aria-label="Toggle menu"
              >
                  {isMobileMenuOpen ? "×" : "☰"}
              </button>
          </div>
        </header>
                
        {/* Mobile menu content */}
        {isMobileMenuOpen && (
            <div className="md:hidden bg-black border-b border-cyan-400 px-4 py-2 animate-fadeIn">
                {isLoggedIn ? (
                    <div className="flex flex-row items-center justify-center gap-3">
                        <span className="text-blue-300 font-medium whitespace-nowrap h-7">
                            Welcome, {userName}
                        </span>
                        <button
                            onClick={handleLogout}
                            className="rounded-md bg-blue-600 hover:bg-blue-700 w-7 h-7 flex items-center justify-center text-white"
                            title="Logout"
                        >
                            ⏻
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-row items-center justify-center gap-6">
                        <Link to="/login" className="text-blue-300 hover:text-yellow-300">
                            Login
                        </Link>
                        <span className="text-blue-500">|</span>
                        <Link to="/signup" className="text-blue-300 hover:text-yellow-300">
                            Register
                        </Link>
                    </div>
                )}
            </div>
        )}  
        <main className="flex-1">
          <div className="mx-auto max-w-screen-lg mt-8">
            <div className="bg-black border border-gray-300 rounded-lg shadow-md p-6">
              {loading ? (
                <Loader />
              ) : (
                <div className="w-full">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-300 text-gray-600">
                        <th className="py-2 px-4 text-left">Date</th>
                        <th className="py-2 px-4 text-left">Remarks</th>
                        <th className="py-2 px-4 text-left">Amount</th>
                        <th className="py-2 px-4 text-left">Balance</th>
                        <th className="py-2 px-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayData.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="py-4 text-center text-yellow-500">No records found!</td>
                        </tr>
                      ) : (
                        displayData.map((mlmAccountItem) => (
                          <React.Fragment key={mlmAccountItem._id}>
                            <tr className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="py-2 px-4 text-gray-800">{formatDateTime(mlmAccountItem.date)}</td>
                              <td className="py-2 px-4 text-gray-800">
                                {getTransactionType(mlmAccountItem.isAdvice, mlmAccountItem.isPayment)}
                              </td>
                              <td className="py-2 px-4 text-right">
                                {mlmAccountItem.isPayment
                                  ? <span className="text-green-600">+₹{mlmAccountItem.amount.toFixed(2)}</span>
                                  : <span className="text-red-600">-₹{mlmAccountItem.amount.toFixed(2)}</span>
                                }
                              </td>
                              <td className="py-2 px-4 text-right text-gray-800">₹{mlmAccountItem.balance.toFixed(2)}</td>
                              <td className="py-2 px-4 text-center">
                                <Tooltip text="View details" position="top">
                                  <span className="text-blue-500 cursor-pointer" onClick={() => handleMLMAccountView(mlmAccountItem._id)}>
                                    <i className="fa-solid fa-eye"></i>
                                  </span>
                                </Tooltip>
                              </td>
                            </tr>
                            {selectedMLMAccountId === mlmAccountItem._id && (
                              <tr>
                                <td colSpan="5" className="bg-gray-200 py-4 px-6 text-gray-700">
                                  <p><strong>Date:</strong> {formatDateTime(mlmAccountItem.date)}</p>
                                  <p><strong>Particulars:</strong> {mlmAccountItem.remarks}</p>
                                  <p><strong>Amount:</strong> ₹{mlmAccountItem.amount.toFixed(2)}</p>
                                  <p><strong>Mode:</strong> {mlmAccountItem.paymentMode}</p>
                                  <p><strong>Transaction ID:</strong> {mlmAccountItem.transactionId}</p>
                                  <p><strong>Payment Details:</strong> {mlmAccountItem.paymentDetails}</p>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
        {/* Footer */}
        <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
            <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="text-xs lg:text-sm">
                    © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
                </div>
                <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
                    <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
                    <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
                    <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
                    <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
                </div>
            </div>
        </footer>
      </div>
  );  
};

export default OrderFlowMyAccount;